import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { PoundSterling, Home, Brain, Briefcase, Users, Menu, FolderOpen, CalendarDays, Settings, Lock, Info, FileText, ChevronRight, Shield, LogOut, BookOpen, MessageCircle, PawPrint, type LucideIcon } from 'lucide-react';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useApp } from '@/contexts/AppContext';
import { useUserRole } from '@/hooks/useUserRole';
import { useContentHealth } from '@/hooks/useContentHealth';
import { useAttentionItems } from '@/hooks/useAttentionItems';
import { format, formatDistanceToNow } from 'date-fns';
import { useLifeCheck } from '@/hooks/useLifeCheck';
import { getLifeCheckAgeDays, getTopLifeCheckPriority, isLifeCheckStale } from '@/lib/life-check';
import { ADULTING_OPEN_ATTENTION_EVENT } from '@/lib/attention-overlay';
import AppHeader from '@/components/AppHeader';
import { useQuickReminderComposer } from '@/components/QuickReminderComposer';
import LegalLinks from '@/components/LegalLinks';
import {
  buildAttentionSurfaceSections,
  dismissAttentionItem,
  filterDismissedAttentionItems,
  getAttentionSurfaceBadge,
  groupAttentionSurfaceItems,
  pruneAttentionDismissals,
  type AttentionDismissalMap,
} from '../../ui-foundation/features/notifications';
import {
  AppBottomSheet,
  AppBottomSheetHeader,
  AppButton,
  AppConnectionBanner,
  AppContentContainer,
  AppEmptyState,
  AppPanelButton,
  AppShell,
  AppShellMain,
  AppWebButton,
} from '../../ui-foundation';
import {
  AppSheet as Sheet,
  AppAvatar as Avatar,
  AppAvatarFallback as AvatarFallback,
  AppAvatarImage as AvatarImage,
  AppSheetContent as SheetContent,
  AppSheetDescription as SheetDescription,
  AppSheetHeader as SheetHeader,
  AppSheetTitle as SheetTitle,
  AppSheetTrigger as SheetTrigger,
} from '../../ui-foundation/adapters/web';
import {
  ADULTING_DESTINATION_NAV_LABELS,
  ADULTING_NAVIGATION_PREFERENCES_CHANGED_EVENT,
  ADULTING_PINNED_SECTIONS_STORAGE_KEY,
  getAvailableAdultingSections,
  getAdultingOverflowDestinations,
  resolveAdultingPinnedDestinations,
  type AdultingNavigationDestinationPath,
} from '../../ui-foundation/core';
import { hasTrackedPets } from '../../ui-foundation/features/pets';

const ATTENTION_DISMISSALS_STORAGE_KEY = 'adulting_attention_dismissals_v1';

type MenuItem = {
  icon: LucideIcon;
  label: string;
  path: string;
};

const BASE_MENU_SECTIONS: Array<{ label: string; items: MenuItem[] }> = [
  {
    label: 'Your tools',
    items: [
      { icon: FileText, label: 'Life Check', path: '/life-check' },
      { icon: BookOpen, label: 'Support Hub', path: '/guides' },
      { icon: FolderOpen, label: 'Documents', path: '/documents' },
      { icon: CalendarDays, label: 'Calendar', path: '/calendar' },
      { icon: Brain, label: 'Brain Dump', path: '__quick_reminder__' },
    ],
  },
  {
    label: 'Community',
    items: [
      { icon: MessageCircle, label: 'Direct messages', path: '/community?view=messages' },
    ],
  },
  {
    label: 'Account',
    items: [
      { icon: Settings, label: 'Profile & settings', path: '/profile' },
    ],
  },
  {
    label: 'Legal',
    items: [
      { icon: Info, label: 'About Adulting', path: '/about' },
      { icon: FileText, label: 'Terms & Conditions', path: '/terms' },
      { icon: Lock, label: 'Privacy policy', path: '/privacy' },
      { icon: Shield, label: 'GDPR & data rights', path: '/gdpr' },
      { icon: Shield, label: 'Code of conduct', path: '/community-guidelines' },
      { icon: MessageCircle, label: 'Contact & complaints', path: '/contact-complaints' },
    ],
  },
];

function profileDisplayName(firstName?: string, email?: string | null) {
  const trimmedFirstName = firstName?.trim();
  if (trimmedFirstName) return trimmedFirstName;

  return email?.split('@')[0]?.trim() || 'Your profile';
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { openQuickReminder } = useQuickReminderComposer();
  const { data, signOut, requiresMfa, requiresPrivilegedMfaSetup, refreshData, user } = useApp();
  const { isAdminOrMod, isAdmin } = useUserRole();
  const { totalDue } = useContentHealth();
  const { items: attentionItems } = useAttentionItems();
  const { latestLifeCheck, loading: lifeCheckLoading } = useLifeCheck();
  const [menuOpen, setMenuOpen] = useState(false);
  const [attentionOpen, setAttentionOpen] = useState(false);
  const [attentionExpanded, setAttentionExpanded] = useState(false);
  const [navigationPreferenceRevision, setNavigationPreferenceRevision] = useState(0);
  const mainRef = useRef<HTMLElement | null>(null);
  const pullStartYRef = useRef<number | null>(null);
  const pullTriggeredRef = useRef(false);
  const [attentionDismissals, setAttentionDismissals] = useState<AttentionDismissalMap>(() => {
    try {
      return JSON.parse(localStorage.getItem(ATTENTION_DISMISSALS_STORAGE_KEY) || '{}');
    } catch {
      return {};
    }
  });
  const currentProfileLabel = profileDisplayName(data.profile.firstName, user?.email);
  const hasPetsMenu = hasTrackedPets(data.pets);

  const tabs = useMemo(() => {
    const { workType, dependantTypes, children } = data.profile;
    const hasChildren = children.some((child) => child.name.trim().length > 0);
    const sectionContext = {
      workType,
      dependantTypes,
      hasChildren,
      partnerName: data.profile.partnerName,
      hasHouseholdShare: Boolean(data.householdShare),
      communityAccessStatus: data.profile.communityAccessStatus,
      hasTrackedPets: hasPetsMenu,
    };
    const availableSections = getAvailableAdultingSections(sectionContext);
    const preferredPinnedDestinations = (() => {
      try {
        const rawValue = localStorage.getItem(ADULTING_PINNED_SECTIONS_STORAGE_KEY);
        return rawValue ? JSON.parse(rawValue) : null;
      } catch {
        return null;
      }
    })();
    const visibleDestinations = resolveAdultingPinnedDestinations({
      preferred: preferredPinnedDestinations,
      context: sectionContext,
    });
    const overflowDestinations = getAdultingOverflowDestinations({
      preferred: preferredPinnedDestinations,
      context: sectionContext,
    });
    const showFamily = availableSections.includes('/family');
    const showWork = availableSections.includes('/work');
    const destinationTabMeta: Record<(typeof visibleDestinations)[number], { to: string; icon: LucideIcon; label: string }> = {
      '/home': { to: '/home', icon: Home, label: 'Home' },
      '/family': { to: '/family', icon: Users, label: 'People' },
      '/money': { to: '/money', icon: PoundSterling, label: 'Money' },
      '/health': { to: '/health', icon: Brain, label: 'Health' },
      '/work': { to: '/work', icon: Briefcase, label: 'Work' },
      '/community': { to: '/community', icon: Users, label: 'Community' },
      '/pets': { to: '/pets', icon: PawPrint, label: 'Pets' },
    };

    localStorage.setItem('adulting_show_family', JSON.stringify(showFamily));
    localStorage.setItem('adulting_show_work', JSON.stringify(showWork));

    const sectionTabs = visibleDestinations.map((path) => destinationTabMeta[path]);

    return {
      sectionTabs,
      overflowDestinations,
    };
  }, [
    data.householdShare,
    data.pets,
    data.profile.children,
    data.profile.communityAccessStatus,
    data.profile.dependantTypes,
    data.profile.partnerName,
    data.profile.workType,
    hasPetsMenu,
    navigationPreferenceRevision,
  ]);

  useEffect(() => {
    const sections = ['/money', '/home', '/health', '/work', '/family'];
    if (sections.includes(location.pathname)) {
      localStorage.setItem('adulting_last_section', location.pathname);
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleNavigationPreferencesChanged = () => {
      setNavigationPreferenceRevision((current) => current + 1);
    };

    window.addEventListener(ADULTING_NAVIGATION_PREFERENCES_CHANGED_EVENT, handleNavigationPreferencesChanged);
    return () => {
      window.removeEventListener(ADULTING_NAVIGATION_PREFERENCES_CHANGED_EVENT, handleNavigationPreferencesChanged);
    };
  }, []);

  const handleMenuItemClick = (item: MenuItem) => {
    if (item.path === '__quick_reminder__') {
      openQuickReminder();
      setMenuOpen(false);
      return;
    }

    if (item.path) {
      navigate(item.path);
      setMenuOpen(false);
    }
  };

  const handleAttentionItem = (path: string) => {
    navigate(path);
    setAttentionOpen(false);
  };

  useEffect(() => {
    localStorage.setItem(ATTENTION_DISMISSALS_STORAGE_KEY, JSON.stringify(attentionDismissals));
  }, [attentionDismissals]);

  useEffect(() => {
    setAttentionDismissals((current) => pruneAttentionDismissals(attentionItems, current));
  }, [attentionItems]);

  useEffect(() => {
    if (attentionOpen) {
      setAttentionExpanded(false);
    }
  }, [attentionOpen]);

  useEffect(() => {
    const openAttention = () => setAttentionOpen(true);
    window.addEventListener(ADULTING_OPEN_ATTENTION_EVENT, openAttention);

    return () => {
      window.removeEventListener(ADULTING_OPEN_ATTENTION_EVENT, openAttention);
    };
  }, []);

  const visibleAttentionItems = useMemo(
    () => filterDismissedAttentionItems(attentionItems, attentionDismissals),
    [attentionDismissals, attentionItems],
  );
  const attentionGroups = useMemo(
    () => groupAttentionSurfaceItems(visibleAttentionItems),
    [visibleAttentionItems],
  );
  const hasVisibleAttentionItems = visibleAttentionItems.length > 0;
  const hasNotificationItems = attentionGroups.notifications.length > 0;
  const menuSections = useMemo(() => {
    const overflowSectionItems = tabs.overflowDestinations.map((path) => ({
      icon: path === '/family'
        ? Users
        : path === '/work'
          ? Briefcase
          : path === '/money'
            ? PoundSterling
            : path === '/health'
              ? Brain
              : path === '/community'
                ? Users
                : path === '/pets'
                  ? PawPrint
                  : Home,
      label: ADULTING_DESTINATION_NAV_LABELS[path],
      path: path === '/community' ? '/community' : path,
    }));

    return [
      ...(overflowSectionItems.length
        ? [{ label: 'Sections', items: overflowSectionItems }]
        : []),
      ...BASE_MENU_SECTIONS,
    ];
  }, [tabs.overflowDestinations]);
  const attentionGroupSections = useMemo(
    () =>
      buildAttentionSurfaceSections(visibleAttentionItems, {
        expandedPrompts: attentionExpanded,
        promptPreviewCount: 3,
      }),
    [attentionExpanded, visibleAttentionItems],
  );

  const dismissAttention = (item: (typeof attentionItems)[number]) => {
    setAttentionDismissals((current) => dismissAttentionItem(item, current));
  };

  const lifeCheckStatus = useMemo(() => {
    const latestPriority = getTopLifeCheckPriority(latestLifeCheck?.summary);
    const ageDays = getLifeCheckAgeDays(latestLifeCheck);

    if (lifeCheckLoading) {
      return {
        title: 'Life Check',
        body: 'Loading your latest Life Check so Adulting can show the right next step.',
        actionLabel: 'Open Life Check',
        actionPath: '/life-check',
      };
    }

    if (!latestLifeCheck) {
      return {
        title: 'No Life Check saved yet',
        body: 'Run one to tell Adulting what matters first across money, home, health, people, and work.',
        actionLabel: 'Run Life Check',
        actionPath: '/life-check',
      };
    }

    if (isLifeCheckStale(latestLifeCheck)) {
      return {
        title: 'Time for a fresh Life Check',
        body: ageDays === null
          ? 'Run it again if your week has shifted.'
          : `Last saved ${formatDistanceToNow(new Date(latestLifeCheck.createdAt), { addSuffix: true })}. Run it again if your week has shifted.`,
        actionLabel: 'Run Life Check',
        actionPath: '/life-check',
      };
    }

    if (latestPriority) {
      return {
        title: 'Latest Life Check',
        body: `${latestPriority.label} needs attention next. ${latestPriority.reason}`,
        actionLabel: `Open ${latestPriority.label}`,
        actionPath: latestPriority.path,
      };
    }

    return {
      title: 'Latest Life Check',
      body: latestLifeCheck.summary.headline,
      actionLabel: 'Open Life Check',
      actionPath: '/life-check',
    };
  }, [latestLifeCheck, lifeCheckLoading]);

  const ROUTE_TITLES: Record<string, string> = {
    '/money': 'My Money',
    '/home': 'My Home',
    '/health': 'My Health',
    '/family': 'My People',
    '/work': 'My Work',
    '/profile': 'My Settings',
    '/guides': 'Support Hub',
    '/settings': 'My Settings',
    '/documents': 'My Documents',
    '/calendar': 'My Calendar',
    '/community': 'My Community',
    '/pets': 'My Pets',
    '/life-check': 'Life Check',
    '/about': 'About Adulting',
    '/privacy': 'Privacy policy',
    '/gdpr': 'GDPR & data rights',
    '/terms': 'Terms & Conditions',
  };
  const pageTitle = location.pathname.startsWith('/guides/')
    ? 'Support detail'
    : ROUTE_TITLES[location.pathname];
  const attentionPullEnabled = false;

  const handleTouchStart = (event: React.TouchEvent<HTMLElement>) => {
    if (!attentionPullEnabled || attentionOpen) {
      return;
    }

    if ((mainRef.current?.scrollTop || 0) > 0) {
      pullStartYRef.current = null;
      return;
    }

    pullStartYRef.current = event.touches[0]?.clientY ?? null;
    pullTriggeredRef.current = false;
  };

  const handleTouchMove = (event: React.TouchEvent<HTMLElement>) => {
    if (!attentionPullEnabled || attentionOpen || pullTriggeredRef.current) {
      return;
    }

    const startY = pullStartYRef.current;
    if (startY == null || (mainRef.current?.scrollTop || 0) > 0) {
      return;
    }

    const currentY = event.touches[0]?.clientY ?? startY;
    if (currentY - startY >= 72) {
      pullTriggeredRef.current = true;
      setAttentionOpen(true);
    }
  };

  const clearPullState = () => {
    pullStartYRef.current = null;
    pullTriggeredRef.current = false;
  };

  return (
    <AppShell className="min-h-screen">
      <AppShellMain asChild>
        <main
          ref={mainRef}
          className="flex-1 overflow-y-auto pb-28 md:pb-36"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={clearPullState}
          onTouchCancel={clearPullState}
        >
          <AppHeader
            title={pageTitle}
            onBellTap={() => setAttentionOpen(true)}
            onQuickAddTap={() => openQuickReminder()}
            attentionCount={visibleAttentionItems.length}
            hasNotificationItems={hasNotificationItems}
          />
          <AppContentContainer pt="$md">
            <AppConnectionBanner onRetry={() => void refreshData()} />
          </AppContentContainer>
          {children}
          <AppContentContainer pb="$xl" pt="$lg">
            <div className="border-t border-border pt-4">
              <LegalLinks />
            </div>
          </AppContentContainer>
        </main>
      </AppShellMain>
      <nav className="fixed inset-x-0 bottom-0 z-50 px-3 pb-2 safe-bottom md:px-6 md:pb-4">
        <AppContentContainer px="$xs">
          <div className="adulting-nav-shadow rounded-[28px] border border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/85">
          <div className="flex min-h-[76px] items-center justify-between gap-1 px-2 py-2 md:min-h-[84px] md:px-3">
          {tabs.sectionTabs.map(({ to, icon: Icon, label }) => {
            const isActive = location.pathname.startsWith(to);
            return (
              <NavLink
                key={to}
                to={to}
                className={`flex min-w-0 flex-1 basis-0 flex-col items-center justify-center gap-1 rounded-2xl px-2 py-2 text-center transition-colors ${
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground'
                }`}
              >
                <Icon
                  size={22}
                  className={isActive ? 'text-primary' : 'text-current'}
                  strokeWidth={isActive ? 2.5 : 1.8}
                />
                <span className={`text-[12px] font-semibold leading-none sm:text-[12px] md:text-[13px] ${isActive ? 'text-primary' : 'text-current'}`}>
                  {label}
                </span>
              </NavLink>
            );
          })}

          {/* More menu */}
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <AppWebButton variant="ghost" className={`h-auto min-w-0 flex-1 basis-0 flex-col items-center justify-center gap-1 rounded-2xl px-2 py-2 text-center transition-colors ${
                menuOpen
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground'
              }`}>
                <Menu
                  size={22}
                  className={menuOpen ? 'text-primary' : 'text-current'}
                  strokeWidth={menuOpen ? 2.5 : 1.8}
                />
                <span className={`text-[12px] font-semibold leading-none sm:text-[12px] md:text-[13px] ${menuOpen ? 'text-primary' : 'text-current'}`}>
                  More
                </span>
              </AppWebButton>
            </SheetTrigger>
            <SheetContent side="bottom" className="rounded-t-2xl px-0 pb-8">
              <SheetHeader className="px-5 pb-2">
                <SheetTitle className="text-lg font-bold text-foreground">My Settings</SheetTitle>
                <SheetDescription className="text-xs">
                  Open the rest of your Adulting tools, settings, and account actions.
                </SheetDescription>
              </SheetHeader>

              <div className="space-y-1 px-2 animate-fade-in">
                <AppPanelButton
                  onClick={() => {
                    navigate('/profile');
                    setMenuOpen(false);
                  }}
                  className="gap-3 rounded-lg bg-[#F5EEF4] hover:bg-[#ECE2EA]"
                >
                  <Avatar className="h-11 w-11 border border-border/70 bg-background">
                    {data.profile.avatarUrl ? <AvatarImage src={data.profile.avatarUrl} alt="Profile photo" /> : null}
                    <AvatarFallback className="bg-primary/15 text-primary text-base font-semibold">
                      {(data.profile.firstName || user?.email || 'A').trim().charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1 text-left">
                    <p className="truncate text-sm font-medium text-foreground">
                      {currentProfileLabel}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {user?.email || 'Profile settings and photo'}
                    </p>
                  </div>
                  <ChevronRight size={18} className="text-primary" strokeWidth={1.8} />
                </AppPanelButton>
                <div className="rounded-lg border border-border bg-muted/40 px-4 py-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    {lifeCheckStatus.title}
                  </p>
                  <p className="mt-1 text-sm font-medium text-secondary">
                    {lifeCheckStatus.body}
                  </p>
                  <AppButton
                    size="sm"
                    className="mt-3"
                    onPress={() => {
                      navigate(lifeCheckStatus.actionPath);
                      setMenuOpen(false);
                    }}
                  >
                    {lifeCheckStatus.actionLabel}
                  </AppButton>
                </div>
                {isAdminOrMod && (
                  <AppPanelButton
                    onClick={() => {
                      const path = requiresPrivilegedMfaSetup
                        ? '/secure-account'
                        : requiresMfa
                          ? '/verify-2fa'
                          : '/admin';
                      navigate(path);
                      setMenuOpen(false);
                    }}
                    className="gap-3 rounded-lg bg-[#F5EEF4] hover:bg-[#ECE2EA]"
                  >
                    <Shield size={20} className="text-primary" strokeWidth={1.8} />
                    <span className="text-sm font-medium text-foreground flex-1">Admin & Moderation</span>
                    {isAdmin && totalDue > 0 && (
                      <span className="min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-1">
                        {totalDue}
                      </span>
                    )}
                  </AppPanelButton>
                )}
                {menuSections.map((section, sectionIndex) => (
                  <div key={section.label} className="space-y-1">
                    {sectionIndex > 0 ? <div className="mx-4 my-2 border-t border-border" /> : null}
                    <p className="px-2 pt-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                      {section.label}
                    </p>
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <AppPanelButton
                          key={item.label}
                          onClick={() => handleMenuItemClick(item)}
                          className="gap-3 rounded-lg hover:bg-muted"
                        >
                          <Icon size={20} className="text-secondary" strokeWidth={1.8} />
                          <span className="text-sm font-medium text-foreground flex-1">{item.label}</span>
                        </AppPanelButton>
                      );
                    })}
                  </div>
                ))}

                <div className="mx-4 my-1 border-t border-border" />
                <AppPanelButton
                  onClick={() => { signOut(); setMenuOpen(false); }}
                  className="gap-3 rounded-lg hover:bg-muted"
                >
                  <LogOut size={20} className="text-destructive" strokeWidth={1.8} />
                  <span className="text-sm font-medium text-destructive">Sign out</span>
                </AppPanelButton>
              </div>
            </SheetContent>
          </Sheet>
          </div>
          </div>
        </AppContentContainer>
      </nav>

      {/* Attention overlay */}
      {attentionOpen && (
        <AppBottomSheet onClose={() => setAttentionOpen(false)} surfaceClassName="max-h-[65vh]">
          <AppBottomSheetHeader
            title="Things To Do"
            subtitle={format(new Date(), 'EEEE, d MMMM')}
            className="pb-4"
            action={(
              <AppButton size="sm" tone="outline" onPress={() => openQuickReminder()}>
                Brain Dump
              </AppButton>
            )}
          />

	          <div className="flex-1 overflow-y-auto px-5">
	              {hasVisibleAttentionItems ? (
	                <div className="space-y-4">
	                  {attentionGroupSections.map((group) => (
	                    <div key={group.key} className="space-y-3">
	                      <div>
	                        <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
	                          {group.title}
	                        </p>
	                        <p className="mt-1 text-sm text-muted-foreground">{group.subtitle}</p>
	                      </div>
	                      {group.items.map((item) => (
	                        <div key={item.id} className="rounded-2xl border border-border bg-card px-4 py-4">
	                          <AppPanelButton
	                            onClick={() => handleAttentionItem(item.path)}
	                            className="gap-3 rounded-xl px-0 py-0 hover:bg-transparent"
	                          >
	                            <div className="flex w-full items-start gap-3 text-left">
	                              <span className="text-xl shrink-0">{item.emoji}</span>
	                              <div className="flex-1 min-w-0">
	                                <div className="flex flex-wrap items-center gap-2">
	                                  <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
	                                    {item.label}
	                                  </p>
                                    {(() => {
                                      const badge = getAttentionSurfaceBadge(item);
                                      if (!badge) return null;
                                      return (
                                        <span
                                          className={
                                            badge.tone === 'danger'
                                              ? 'rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-destructive'
                                              : badge.tone === 'warning'
                                                ? 'rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-amber-700 dark:text-amber-300'
                                                : 'rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-primary'
                                          }
                                        >
                                          {badge.text}
                                        </span>
                                      );
                                    })()}
	                                </div>
	                                <p className="mt-0.5 text-sm font-medium text-secondary">{item.text}</p>
	                              </div>
	                              <ChevronRight size={18} className="mt-0.5 shrink-0 text-muted-foreground" strokeWidth={1.8} />
	                            </div>
	                          </AppPanelButton>
	                          <div className="mt-3 flex flex-wrap gap-2">
	                            <AppButton size="sm" tone="outline" onPress={() => dismissAttention(item)}>
	                              Hide for now
	                            </AppButton>
	                          </div>
	                        </div>
	                      ))}
	                      {group.key === 'prompts' && (group.totalCount || 0) > 3 ? (
	                        <AppButton tone="outline" size="sm" onPress={() => setAttentionExpanded((value) => !value)}>
	                          {attentionExpanded ? 'Show fewer prompts' : `Show ${(group.totalCount || 0) - 3} more prompts`}
	                        </AppButton>
	                      ) : null}
	                    </div>
	                  ))}
	                </div>
	              ) : (
                <AppEmptyState
                  title="Nothing overdue or due soon right now."
                  body="Run a fresh Life Check if your week has shifted."
                  action={(
                    <AppButton size="sm" onPress={() => handleAttentionItem('/life-check')}>
                      Open Life Check
                    </AppButton>
                  )}
                />
              )}
          </div>

          <div className="px-5 pt-3 pb-6">
              <div className="flex flex-wrap gap-2">
                <AppButton tone="outline" onPress={() => handleAttentionItem('/guides')}>
                  Ask Adulting
                </AppButton>
                <AppButton tone="outline" onPress={() => setAttentionOpen(false)}>
                  Dismiss
                </AppButton>
              </div>
          </div>
        </AppBottomSheet>
      )}
    </AppShell>
  );
}
